import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import classNames from "classnames";
import { ITreeData } from "../Hierarchy/CustomTreeDropdown/CustomDropdown";
import { getOrganizationTrees } from "../../../core/api/organizations/organizations";
import { transformOrganizationsToTreeData } from "../../../core/utils/organizations";
import { notifyApiErrors } from "../../../core/helpers/helpers";
import useTranslations from "../../../core/i18n/useTranslations";
import PrimeModal from "../PrimeModal/PrimeModal";
import useUser from "../../../core/user/useUser";
import LoadingSpinner from "../Loading/LoadingSpinner";

import styles from "./ChildrenTreePopup.module.css";

const ChildrenTreeModal = ({
  organizationId,
  isOpen,
  onClose = () => {},
}: {
  organizationId?: number;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const t = useTranslations();
  const { authenticatedRequest } = useUser();
  const [isLoading, setIsLoading] = useState(true);
  const [branchData, setBranchData] = useState<ITreeData[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchOrganizationsTree = async () => {
    try {
      setIsLoading(true);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const response = await getOrganizationTrees(
        {
          parentId: organizationId,
          includeParent: true,
          getRoot: true,
        },
        authenticatedRequest,
        abortControllerRef.current.signal
      );
      setBranchData(
        transformOrganizationsToTreeData(response.data.organizations)
      );
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    } finally {
      setIsLoading(false);
    }
  };

  const renderChildren = (child: ITreeData, step = 1) => (
    <div
      data-cy="tree_child_wrapper"
      key={`${child.key}_key`}
      style={{ paddingLeft: "12px" }}
    >
      <Link
        to={`/adminportal/organizations/${child.key}`}
        className={styles.popupLink}
      >
        <p
          className={
            child.key === organizationId
              ? styles.popupRootChild
              : styles.popupSecondaryChild
          }
          key={child.key}
          data-cy="child_organization"
        >
          {child.label}
        </p>
      </Link>
      {child?.children?.length
        ? child.children.map((innerChild) =>
            renderChildren(innerChild, step + 1)
          )
        : null}
    </div>
  );

  useEffect(() => {
    if (isOpen && organizationId) {
      fetchOrganizationsTree();
    }
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [organizationId, isOpen]);

  if (!organizationId) return null;

  return (
    <PrimeModal
      withHeader
      withFooter
      isOpen={isOpen}
      onClose={onClose}
      header={t("Organizations:organizations_hierarchy")}
      submitBtn={{
        text: t("Common:close"),
        variant: "secondary",
        onClick: onClose,
      }}
      fixedWidth="848px"
      dataCy="hierarchy_modal"
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          {branchData.length > 0 &&
            branchData.map((org) => (
              <div key={org.key} className={styles.rootWrapper}>
                <Link
                  to={`/adminportal/organizations/${org.key}`}
                  className={styles.popupLink}
                >
                  <p
                    className={classNames({
                      [styles.popupRoot]: org.key !== organizationId,
                      [styles.popupMainChild]: org.key === organizationId,
                    })}
                    data-cy="parent_organization"
                  >
                    {org.label}
                  </p>
                </Link>
                {org.children?.map((child: ITreeData) => renderChildren(child))}
              </div>
            ))}
        </>
      )}
    </PrimeModal>
  );
};

export default ChildrenTreeModal;
